import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Signal, CheckCircle2, MessageCircle, X, Wifi, PhoneCall, BadgeCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

export default function Plans() {
  const { waLink, settings } = useSettings();
  const [plans, setPlans] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", current_operator: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/plan-requests", { plan_id: selected.id, ...form });
      setConfirmation(r.data);
      toast.success("Demande envoyée !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const hasWhatsApp = Boolean((settings?.whatsapp_number || "").trim());

  if (confirmation) {
    return (
      <div className="container-oxy max-w-xl py-12" data-testid="plan-confirmation">
        <div className="card-oxy p-6 text-center sm:p-10">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Demande envoyée</h1>
          <p className="mt-2 text-sm text-slate-600">
            Référence <span className="font-mono font-bold text-gold-dark" data-testid="plan-reference">{confirmation.reference}</span><br />
            Forfait : <strong>{confirmation.plan_name}</strong>
          </p>
          <p className="mt-4 rounded-2xl bg-gold-soft p-4 text-sm text-slate-700">
            Passez en boutique avec une <strong>pièce d'identité</strong> et un <strong>RIB</strong> :
            on s'occupe de la souscription et de la portabilité de votre numéro.
          </p>
          {hasWhatsApp && (
            <a href={waLink(`Bonjour Oxyservices, je confirme ma demande de forfait ${confirmation.reference} (${confirmation.plan_name}).`)}
              target="_blank" rel="noopener noreferrer" data-testid="plan-whatsapp-btn" className="btn-gold mt-6 w-full">
              <MessageCircle className="h-4 w-4" /> Confirmer sur WhatsApp
            </a>
          )}
          <button onClick={() => { setConfirmation(null); setSelected(null); }} className="btn-outline-oxy mt-3 w-full">
            Voir les forfaits
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-oxy py-10 sm:py-14" data-testid="plans-page">
      <p className="eyebrow">Forfaits mobiles</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
        Souscrivez votre forfait <span className="text-gold-dark">en boutique</span>
      </h1>
      <p className="mt-2 max-w-xl text-sm text-slate-600 sm:text-base">
        Choisissez votre forfait, laissez-nous vos coordonnées : on prépare votre souscription et la
        portabilité de votre numéro. Finalisation en boutique, sans stress.
      </p>

      {plans === null ? (
        <div className="mt-8 grid gap-4 md:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-3xl bg-slate-100" />)}</div>
      ) : plans.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="plans-empty">
          <Signal className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-display font-semibold text-slate-800">Nos offres arrivent bientôt</p>
          <p className="mt-1 text-sm text-slate-500">Contactez-nous directement pour connaître nos forfaits disponibles.</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:gap-6 md:grid-cols-3" data-testid="plans-grid">
          {plans.map((p, i) => (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`card-oxy relative flex flex-col p-6 animate-fade-up ${i === 1 ? "border-gold !shadow-lg ring-2 ring-gold/40" : ""}`}
              style={{ animationDelay: `${i * 90}ms` }}>
              {i === 1 && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-ink shadow">
                  Le plus choisi
                </span>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-gold-dark">{p.operator || "Opérateur"}</span>
                {p.demo && <span className="rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">Exemple</span>}
              </div>
              <h2 className="mt-2 font-display text-xl font-bold text-slate-900">{p.name}</h2>
              <p className="mt-3">
                <span className="font-display text-4xl font-extrabold text-ink">{p.price} €</span>
                <span className="text-sm font-semibold text-slate-500"> /mois</span>
              </p>
              <ul className="mt-5 flex-1 space-y-2.5 text-sm text-slate-700">
                <li className="flex items-center gap-2.5"><Wifi className="h-4 w-4 shrink-0 text-gold-dark" /><strong>{p.data}</strong>&nbsp;d'internet mobile</li>
                <li className="flex items-center gap-2.5"><PhoneCall className="h-4 w-4 shrink-0 text-gold-dark" />{p.calls}</li>
                {p.features?.map((f) => (
                  <li key={f} className="flex items-center gap-2.5"><BadgeCheck className="h-4 w-4 shrink-0 text-gold-dark" />{f}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs font-semibold text-emerald-700">{p.commitment}</p>
              <button data-testid={`plan-subscribe-${p.id}`} onClick={() => { setSelected(p); setConfirmation(null); }}
                className={`mt-5 w-full ${i === 1 ? "btn-gold" : "btn-ink"}`}>
                Souscrire
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="mt-8 text-center text-xs text-slate-400">
        Souscription et portabilité finalisées en boutique — pièce d'identité et RIB nécessaires.
      </p>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-4 backdrop-blur-sm sm:items-center" data-testid="plan-modal"
          onClick={(e) => e.target === e.currentTarget && setSelected(null)}>
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl animate-fade-up">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900">{selected.name}</h2>
                <p className="text-sm font-semibold text-gold-dark">{selected.price} €/mois · {selected.data}</p>
              </div>
              <button data-testid="plan-modal-close" onClick={() => setSelected(null)} className="rounded-full bg-slate-100 p-2 text-slate-500"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="label-oxy" htmlFor="plan-name">Nom complet</label>
                <input id="plan-name" data-testid="plan-name-input" required maxLength={80} value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Votre nom" className="input-oxy" />
              </div>
              <div>
                <label className="label-oxy" htmlFor="plan-phone">Téléphone</label>
                <input id="plan-phone" data-testid="plan-phone-input" required type="tel" maxLength={20} value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="06 12 34 56 78" className="input-oxy" />
              </div>
              <div>
                <label className="label-oxy" htmlFor="plan-operator">Opérateur actuel <span className="font-normal text-slate-400">(optionnel)</span></label>
                <input id="plan-operator" data-testid="plan-operator-input" maxLength={60} value={form.current_operator}
                  onChange={(e) => setForm((f) => ({ ...f, current_operator: e.target.value }))} placeholder="Orange, SFR, Free…" className="input-oxy" />
              </div>
              <button type="submit" data-testid="plan-submit-btn" disabled={loading} className="btn-gold w-full disabled:opacity-60">
                {loading ? "Envoi…" : "Envoyer ma demande"}
              </button>
              <p className="text-center text-xs text-slate-400">Nous vous rappelons pour finaliser la souscription en boutique.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
